/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class SuperuserBillingAdminService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Get credit balance and billing mode for a company (superuser only)
   * @param companyUuid UUID of the company
   * @returns any Account billing details retrieved successfully
   * @throws ApiError
   */
  public getApiServiceorganizationsProcessesSuperuserBillingAccounts(
    companyUuid: string,
  ): CancelablePromise<{
    data?: {
      companyCredit?: Record<string, any>;
    };
  }> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/serviceorganizations/processes/superuser/billing/accounts/{companyUuid}',
      path: {
        'companyUuid': companyUuid,
      },
      errors: {
        403: `Forbidden - superuser role required`,
        500: `Internal server error`,
      },
    });
  }

  /**
   * Get paginated transaction history for a specific company (superuser only)
   * @param companyUuid UUID of the company
   * @param cursor Pagination cursor
   * @param limit Number of records per page (max 100)
   * @param startDate Filter events at or after this date
   * @param endDate Filter events at or before this date
   * @param eventType Filter by event type
   * @returns any Transaction history retrieved successfully
   * @throws ApiError
   */
  public getApiServiceorganizationsProcessesSuperuserBillingAccountsTransactionHistory(
    companyUuid: string,
    cursor?: string,
    limit: number = 50,
    startDate?: string,
    endDate?: string,
    eventType?: 'DEBIT' | 'CREDIT',
  ): CancelablePromise<{
    data?: {
      events?: Array<Record<string, any>>;
      pagination?: {
        limit?: number;
        hasNextPage?: boolean;
        nextCursor?: string | null;
      };
    };
  }> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/serviceorganizations/processes/superuser/billing/accounts/{companyUuid}/transactionHistory',
      path: {
        'companyUuid': companyUuid,
      },
      query: {
        'cursor': cursor,
        'limit': limit,
        'startDate': startDate,
        'endDate': endDate,
        'eventType': eventType,
      },
      errors: {
        403: `Forbidden - superuser role required`,
        500: `Internal server error`,
      },
    });
  }

  /**
   * Add or adjust credits for a company (superuser only, fully audited)
   * @param requestBody
   * @returns any Credits adjusted successfully
   * @throws ApiError
   */
  public postApiServiceorganizationsProcessesSuperuserBillingCreditsAdd(
    requestBody: {
      /**
       * UUID of the company to add credits to
       */
      companyUuid: string;
      /**
       * Number of credits to add
       */
      creditsAmount: number;
      /**
       * Audit reason for the credit adjustment
       */
      reason: string;
      /**
       * How the credits were acquired
       */
      purchasePath?: 'IN_PRODUCT_PURCHASE' | 'SALES_CONTRACT' | 'FOUNTAIN_AD_HOC_GRANT';
      /**
       * Product the credits are earmarked for
       */
      productAllocation?: 'ANNA' | 'SAM' | 'CUE' | 'EMMA' | 'SHARED_POOL';
      /**
       * When the purchase/grant was made — defaults to now
       */
      purchaseDate?: string;
      /**
       * Start of the credit period — defaults to the purchase date
       */
      startDate?: string;
      /**
       * Last day of the credit period (inclusive) — defaults to 12 months after start, minus a day
       */
      endDate?: string;
      /**
       * Amount paid for these credits, in USD cents
       */
      totalPaidAmountCents?: number;
      /**
       * Salesforce opportunity ID
       */
      opportunityId?: string;
    },
  ): CancelablePromise<{
    data?: {
      companyUuid?: string;
      creditsAmount?: number;
      reason?: string;
      newBalance?: number;
    };
  }> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/serviceorganizations/processes/superuser/billing/credits/add',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad request - validation error`,
        403: `Forbidden - superuser role required`,
        500: `Internal server error`,
      },
    });
  }

  /**
   * Toggle allow-overages for a company (superuser only, fully audited)
   * @param requestBody
   * @returns any Allow-overages updated successfully
   * @throws ApiError
   */
  public patchApiServiceorganizationsProcessesSuperuserBillingAllowOverages(
    requestBody: {
      /**
       * UUID of the company to update
       */
      companyUuid: string;
      /**
       * Set to true to allow the company to exceed 0 credits
       */
      allowOverages: boolean;
      /**
       * Audit reason for the change
       */
      reason: string;
    },
  ): CancelablePromise<{
    data?: {
      companyUuid?: string;
      allowOverages?: boolean;
      reason?: string;
      companyCredit?: Record<string, any>;
    };
  }> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/api/serviceorganizations/processes/superuser/billing/allowOverages',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad request - validation error`,
        403: `Forbidden - superuser role required`,
        500: `Internal server error`,
      },
    });
  }

  /**
   * List credit disputes (superuser only)
   * @param status Filter by dispute status
   * @param companyUuid Filter by company UUID
   * @returns any Disputes retrieved successfully
   * @throws ApiError
   */
  public getApiServiceorganizationsProcessesSuperuserBillingDisputes(
    status?: 'PENDING' | 'APPROVED' | 'DENIED',
    companyUuid?: string,
  ): CancelablePromise<{
    data?: Record<string, any>;
  }> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/serviceorganizations/processes/superuser/billing/disputes',
      query: {
        'status': status,
        'companyUuid': companyUuid,
      },
      errors: {
        403: `Forbidden - superuser role required`,
        500: `Internal server error`,
      },
    });
  }

  /**
   * Approve or deny a credit dispute, issuing a refund if approved (superuser only)
   * @param disputeUuid UUID of the dispute to resolve
   * @param requestBody
   * @returns any Dispute resolved successfully
   * @throws ApiError
   */
  public patchApiServiceorganizationsProcessesSuperuserBillingDisputes(
    disputeUuid: string,
    requestBody: {
      /**
       * Resolution decision
       */
      status: 'APPROVED' | 'DENIED';
      /**
       * Superuser's resolution note
       */
      resolution: string;
    },
  ): CancelablePromise<{
    data?: Record<string, any>;
  }> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/api/serviceorganizations/processes/superuser/billing/disputes/{disputeUuid}',
      path: {
        'disputeUuid': disputeUuid,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad request - validation error`,
        403: `Forbidden - superuser role required`,
        404: `Dispute not found`,
        409: `Dispute has already been resolved`,
        500: `Internal server error`,
      },
    });
  }

  /**
   * List invoice credit orders pending reconciliation (superuser only)
   * @param companyUuid Filter by company UUID
   * @param status Filter by order status (defaults to all)
   * @param limit Number of records per page (max 100)
   * @param cursor Pagination cursor (createdAt of last record)
   * @returns any Invoice credit orders retrieved successfully
   * @throws ApiError
   */
  public getApiServiceorganizationsProcessesSuperuserBillingInvoiceOrders(
    companyUuid?: string,
    status?: 'PENDING' | 'RECONCILED',
    limit: number = 50,
    cursor?: string,
  ): CancelablePromise<{
    data?: {
      orders?: Array<Record<string, any>>;
      pagination?: {
        limit?: number;
        hasNextPage?: boolean;
        nextCursor?: string | null;
      };
    };
  }> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/serviceorganizations/processes/superuser/billing/invoiceOrders',
      query: {
        'companyUuid': companyUuid,
        'status': status,
        'limit': limit,
        'cursor': cursor,
      },
      errors: {
        403: `Forbidden — superuser role required`,
        500: `Internal server error`,
      },
    });
  }

  /**
   * Mark an invoice credit order as reconciled (superuser only)
   * @param orderUuid UUID of the invoice credit order
   * @param requestBody
   * @returns any Order marked as reconciled
   * @throws ApiError
   */
  public patchApiServiceorganizationsProcessesSuperuserBillingInvoiceOrdersReconcile(
    orderUuid: string,
    requestBody?: {
      /**
       * Optional notes (e.g. invoice number, billing period)
       */
      reconciliationNotes?: string;
    },
  ): CancelablePromise<{
    data?: Record<string, any>;
  }> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/api/serviceorganizations/processes/superuser/billing/invoiceOrders/{orderUuid}/reconcile',
      path: {
        'orderUuid': orderUuid,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        403: `Forbidden — superuser role required`,
        404: `Invoice credit order not found`,
        409: `Order has already been reconciled`,
        500: `Internal server error`,
      },
    });
  }

}
